import { useState } from "react";
import "./HistoricalLocateDetails.css";
import axios from "axios";
import { formatBusinessDate, formatLocateExpiration, formatTimestamp } from "@/utils/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;
const PAGE_SIZE = 50;

const valueText = (value) => {
  if (value === null || value === undefined || value === "") return "Not recorded";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return typeof value === "object" ? JSON.stringify(value) : String(value);
};

const changeTitle = (field) => ({
  permit_created: "Ticket created",
  permit_updated: "Ticket updated",
  permit_history_corrected: "Historical correction",
}[field] || String(field).replaceAll("_", " "));

const displayRecordedTime = (value) =>
  value ? formatTimestamp(value) : "Not recorded";

// Readable values use the same date/time preferences as Communication History.
// The stored before/after values are unchanged and kept in the title attribute.
const displayAuditValue = (field, value) => {
  if (value === null || value === undefined || value === "") return "Not recorded";
  if (typeof value === "string") {
    if (/^\d{4}-\d{2}-\d{2}T/.test(value)) return formatTimestamp(value);
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return formatBusinessDate(value);
  }
  return valueText(value);
};

export default function HistoricalLocateDetails({ record }) {
  const [expanded, setExpanded] = useState(false);
  const [events, setEvents] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchEvents = async (offset) => {
    if (loading) return;
    setLoading(true);
    setError("");
    try {
      const result = await axios.get(`${API}/permits/${encodeURIComponent(record.id)}/history`, {
        params: { offset, limit: PAGE_SIZE },
      });
      setEvents((previous) => offset === 0 ? result.data.events : [...previous, ...result.data.events]);
      setHasMore(Boolean(result.data.has_more));
      setLoaded(true);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Ticket change history could not be loaded.");
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !loaded) fetchEvents(0);
  };

  return (
    <div className="historical-locate-detail" data-testid={`historical-locate-detail-${record.id}`}>
      <button
        aria-expanded={expanded}
        className="board-refresh-button"
        data-testid={`historical-locate-toggle-${record.id}`}
        onClick={toggle}
        type="button"
      >
        {expanded ? "Hide full record" : "View full historical record"}
      </button>
      {expanded && (
        <section className="historical-locate-expanded" data-testid={`historical-locate-expanded-${record.id}`}>
          <dl>
            <dt>Ticket number</dt><dd>{valueText(record.reference_number)}</dd>
            <dt>Requested</dt><dd>{record.request_date ? formatBusinessDate(record.request_date) : "Not recorded"}</dd>
            <dt>Valid through</dt><dd>{formatLocateExpiration(record)}</dd>
            <dt>Exact deadline (as recorded)</dt><dd title={record.expires_at || undefined}>{displayRecordedTime(record.expires_at)}</dd>
            <dt>Job-site time zone</dt><dd>{valueText(record.job_site_time_zone)}</dd>
            <dt>Recorded condition (latest)</dt><dd>{valueText(record.status)}</dd>
            <dt>Ticket description</dt><dd>{valueText(record.ticket_description)}</dd>
            <dt>Office notes</dt><dd>{valueText(record.notes)}</dd>
            <dt>Ticket not needed</dt><dd>{valueText(record.ticket_not_needed)}</dd>
            <dt>Created at</dt><dd><time dateTime={record.created_at || undefined} title={record.created_at || undefined}>{displayRecordedTime(record.created_at)}</time></dd>
            <dt>Replaces ticket</dt><dd>{valueText(record.replaces_id)}</dd>
            <dt>Record active</dt><dd>{valueText(record.active !== false)}</dd>
          </dl>
          <h4>Complete change history</h4>
          {error && <p role="alert">{error}</p>}
          {loading && <p>Loading ticket history…</p>}
          {loaded && !events.length && <p>No audited changes recorded for this ticket.</p>}
          <ol className="historical-locate-events">
            {events.map((event) => (
              <li key={event.id} data-testid={`historical-locate-event-${event.id}`}>
                <strong>{changeTitle(event.action || "Change")}</strong>
                <p className="historical-locate-event-meta">
                  <time dateTime={event.occurred_at || undefined} title={event.occurred_at || undefined}>
                    {displayRecordedTime(event.occurred_at)}
                  </time>
                  {" · "}{valueText(event.actor_name)}
                  {event.actor_role && <span className="historical-locate-event-role"> ({changeTitle(event.actor_role)})</span>}
                </p>
                {event.reason && <p>Correction reason: {event.reason}</p>}
                {(event.changes || []).map((change, index) => (
                  <p key={`${change.field}-${index}`}>
                    <strong>{changeTitle(change.field || "Field")}</strong>:{" "}
                    <span title={valueText(change.before)}>{displayAuditValue(change.field, change.before)}</span>
                    {" → "}
                    <span title={valueText(change.after)}>{displayAuditValue(change.field, change.after)}</span>
                  </p>
                ))}
              </li>
            ))}
          </ol>
          {hasMore && (
            <button disabled={loading} onClick={() => fetchEvents(events.length)} type="button">
              Load older changes
            </button>
          )}
        </section>
      )}
    </div>
  );
}
